import { useState } from "preact/hooks";
import { useResource, type Resource } from "@tenergy/web-core/dashboard";
import { adminReads, adminWrites } from "../api/admin-api.js";
import type { ApiTarget } from "../api/request.js";
import type { AdminRole, DropContext, FunnelPeriod, PromoDropsView, PromoFunnel, PromoTokenView } from "../api/types.js";
import { count, fromUtcInput, toLocalInput, utc } from "../format.js";
import { Address, Blank, Button, ErrorNote, Field, FieldAction, inputClass, Note, Panel, Pill, ScreenHeader, ShortId, Spinner, Table, Td, Th, Tile } from "../ui.js";
import { NileGate } from "./nile.js";
import { WatchDropsSwitch } from "./watch-drops.js";

/**
 * Growth: one page per token (owner 2026-10-20), each an ordinary ad funnel (sent → claimed the bonus → became
 * paying, with the customers who already paid before kept apart), the drops log, and a manual send. Not under Promo
 * codes: those are fee codes for customers we already know (owner: «промо коды это же вообще другая штука»).
 */

/** tenergy.app, mainnet: a dropped wallet claims 65,000 energy for 1 h once. */
export function PromoAppScreen({ role }: { role: AdminRole }) {
  const [refresh, setRefresh] = useState(0);
  const drops = useResource((signal) => {
    void signal;
    return adminReads.promoDrops("mainnet");
  }, [refresh]);
  const [period, setPeriod] = useState<FunnelPeriod>(() => presetPeriod("week"));
  const funnel = useResource((signal) => {
    void signal;
    return adminReads.promoFunnel(period);
  }, [refresh, period.from, period.to]);
  return (
    <>
      <ScreenHeader
        title="tenergy.app"
        lead="Mainnet. Wallets we dropped the tenergy.app token on claim 65,000 energy for 1 h once, by signing on tenergy.me or by forwarding the token. We buy that energy at the cheapest venue."
      />
      <FunnelPanel funnel={funnel} period={period} onPeriod={setPeriod} />
      <PromoTokenPanel role={role} />
      <ManualSendPanel
        role={role}
        tokenName="tenergy.app"
        target="mainnet"
        disabledReason={drops.data != null && drops.data.token_id === null ? "token not issued yet" : null}
        onSent={() => setRefresh((n) => n + 1)}
      />
      <WatchDropsSwitch />
      <PromoDropsPanel role={role} view={drops} target="mainnet" withSwitch={false} />
    </>
  );
}

/**
 * tenergy.dev: drops on Nile, the reward a personal 3 % / 30-day promo code on mainnet. An ad campaign, so it sits in
 * the main menu and reads its Nile half itself; the automatic drops switch lives here.
 */
export function PromoDevScreen() {
  return (
    <>
      <ScreenHeader
        title="tenergy.dev"
        lead="Drops on Nile; a dropped wallet claims a personal DEV- promo code on mainnet: 3 % routing fee for 30 days from entry."
      />
      <NileGate>{(role) => <PromoDevBody role={role} />}</NileGate>
    </>
  );
}

function PromoDevBody({ role }: { role: AdminRole }) {
  const [refresh, setRefresh] = useState(0);
  const drops = useResource((signal) => {
    void signal;
    return adminReads.promoDrops("nile");
  }, [refresh]);
  const [period, setPeriod] = useState<FunnelPeriod>(() => presetPeriod("week"));
  // The cohort is Nile's (who was sent to in the period); claims and payments are mainnet's.
  const funnel = useResource(async (signal) => {
    void signal;
    const cohort = await adminReads.promoCohort(period, "nile");
    return adminReads.promoDevFunnel(period, cohort.addresses);
  }, [refresh, period.from, period.to]);
  return (
    <>
      <FunnelPanel funnel={funnel} period={period} onPeriod={setPeriod} />
      <ManualSendPanel
        role={role}
        tokenName="tenergy.dev"
        target="nile"
        disabledReason={drops.data != null && drops.data.token_id === null ? "token not configured on Nile" : null}
        onSent={() => setRefresh((n) => n + 1)}
      />
      <PromoDropsPanel role={role} view={drops} target="nile" withSwitch />
    </>
  );
}

const rate = (part: number, whole: number) => (whole === 0 ? "—" : `${((part / whole) * 100).toFixed(1)} %`);

const DAY_MS = 86_400_000;
const PRESETS = { day: { label: "Day", days: 1 }, week: { label: "Week", days: 7 }, month: { label: "Month", days: 30 } } as const;
type Preset = keyof typeof PRESETS;

/** The last 24 h, 7 d or 30 d up to now. */
function presetPeriod(preset: Preset): FunnelPeriod {
  const to = Date.now();
  return { from: new Date(to - PRESETS[preset].days * DAY_MS).toISOString(), to: new Date(to).toISOString() };
}

/** UTC dates, both days included: `to` is the next day's 00:00 UTC. */
function datePeriod(fromDate: string, toDate: string): FunnelPeriod | null {
  const from = Date.parse(`${fromDate}T00:00:00Z`);
  const to = Date.parse(`${toDate}T00:00:00Z`) + DAY_MS;
  return Number.isNaN(from) || Number.isNaN(to) || from >= to ? null : { from: new Date(from).toISOString(), to: new Date(to).toISOString() };
}

function PeriodBar({ period, onPeriod }: { period: FunnelPeriod; onPeriod(next: FunnelPeriod): void }) {
  const [fromDate, setFromDate] = useState(period.from.slice(0, 10));
  const [toDate, setToDate] = useState(period.to.slice(0, 10));
  const custom = datePeriod(fromDate, toDate);
  return (
    <div class="mb-3 flex flex-wrap items-end gap-2">
      {(Object.keys(PRESETS) as Preset[]).map((p) => (
        <Button key={p} tone="ghost" onClick={() => onPeriod(presetPeriod(p))}>
          {PRESETS[p].label}
        </Button>
      ))}
      <Field label="From (UTC date)">
        <input type="date" class={inputClass} value={fromDate} onInput={(e) => setFromDate((e.currentTarget as HTMLInputElement).value)} />
      </Field>
      <Field label="To (UTC date, included)">
        <input type="date" class={inputClass} value={toDate} onInput={(e) => setToDate((e.currentTarget as HTMLInputElement).value)} />
      </Field>
      <Button tone="ghost" disabled={custom === null} onClick={() => custom !== null && onPeriod(custom)}>
        Apply range
      </Button>
    </div>
  );
}

function FunnelPanel({ funnel, period, onPeriod }: { funnel: Resource<PromoFunnel>; period: FunnelPeriod; onPeriod(next: FunnelPeriod): void }) {
  const data = funnel.data;
  return (
    <Panel
      title="Funnel"
      subtitle={`Wallets sent to from ${utc(period.from)} to ${utc(period.to)} UTC, and their claims and payments whenever they came. Paying: an order paid from the account's balance after the claim; the free grant does not count. Already customers (topped up and ordered before) are counted as claimed, never as paying.`}
    >
      <PeriodBar period={period} onPeriod={onPeriod} />
      {funnel.loading && data == null ? <Spinner /> : null}
      {funnel.error !== null ? <ErrorNote error={funnel.error} onRetry={() => funnel.reload()} /> : null}
      {data == null || funnel.error !== null ? null : (
        <div class="grid gap-3 sm:grid-cols-4">
          <Tile label="Sent" value={count(data.sent)} />
          <Tile label="Claimed" value={count(data.claimed)} note={`${rate(data.claimed, data.sent)} of sent`} />
          <Tile label="Paying" value={count(data.paying)} note={`${rate(data.paying, data.claimed - data.prior)} of claimed new customers`} />
          <Tile label="Already customers" value={count(data.prior)} note="Claimed, left out of the paying rate" />
        </div>
      )}
    </Panel>
  );
}

const ADDRESS_SHAPE = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;

/**
 * One send now, through the automatic drops' Dropper on the worker: the next sender in turn, its bandwidth checked,
 * the drop logged, the cursor moved. Works with the automatic switch off; a repeat to the same address goes out too.
 */
function ManualSendPanel({
  role,
  tokenName,
  target,
  disabledReason,
  onSent,
}: {
  role: AdminRole;
  tokenName: string;
  target: ApiTarget;
  disabledReason: string | null;
  onSent(): void;
}) {
  const [address, setAddress] = useState("");
  const [tokens, setTokens] = useState("1");
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [queued, setQueued] = useState<string | null>(null);
  if (role !== "editor") return null;
  const trimmed = address.trim();
  const validAddress = ADDRESS_SHAPE.test(trimmed);
  const validTokens = /^[1-9]\d*$/.test(tokens);
  const off = disabledReason !== null;
  const send = () => {
    setSending(true);
    setError(null);
    void adminWrites
      .sendPromoDrop({ address: trimmed, tokens: Number(tokens) }, target)
      .then((r) => {
        setQueued(`${count(r.tokens)} ${tokenName} to ${r.address} queued; the drops table shows it once the worker has sent it.`);
        setConfirming(false);
        setAddress("");
        onSent();
      })
      .catch((cause: unknown) => setError(cause))
      .finally(() => setSending(false));
  };
  return (
    <Panel title="Manual send" subtitle={`Sends ${tokenName} from the next sender in turn, as the automatic drops do. Works with the automatic switch off.`}>
      {off ? <Note tone="warning">Sending is off: {disabledReason}.</Note> : null}
      <div class="mb-3 grid gap-3 sm:grid-cols-4">
        <Field label="Address" hint={trimmed !== "" && !validAddress ? "Not a TRON address" : "The server checks the checksum"}>
          <input class={inputClass} value={address} disabled={off || confirming} onInput={(e) => setAddress((e.currentTarget as HTMLInputElement).value)} />
        </Field>
        <Field label="Tokens" hint="Whole tokens">
          <input class={inputClass} inputMode="numeric" value={tokens} disabled={off || confirming} onInput={(e) => setTokens((e.currentTarget as HTMLInputElement).value)} />
        </Field>
        <FieldAction>
          {confirming ? null : (
            <Button disabled={off || !validAddress || !validTokens} onClick={() => setConfirming(true)}>
              Send…
            </Button>
          )}
        </FieldAction>
      </div>
      {confirming ? (
        <Note tone="warning">
          Send {tokens} {tokenName} to <span class="font-mono">{trimmed}</span>?{" "}
          <Button disabled={sending} onClick={send}>
            Confirm send
          </Button>{" "}
          <Button tone="ghost" disabled={sending} onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        </Note>
      ) : null}
      {queued === null ? null : <Note>{queued}</Note>}
      {error === null ? null : <ErrorNote error={error} />}
    </Panel>
  );
}

const PROMO_STATE: Record<PromoTokenView["state"], { label: string; tone: "good" | "attention" | "neutral" }> = {
  active: { label: "open", tone: "good" },
  cap_reached_today: { label: "today's cap used up", tone: "attention" },
  not_started: { label: "not started", tone: "neutral" },
  ended: { label: "ended", tone: "neutral" },
  off: { label: "off on this network", tone: "neutral" },
};

/**
 * The tenergy.app promo token (docs/product/promo-redeem.md, owner 2026-10-19): an address we dropped the token on
 * claims 65,000 energy for 1 h once, by signing on tenergy.me or by forwarding the token. The cap per UTC day and the
 * window are set here (owner: «в админке можно было его выставлять и смотреть кто редимнул»); viewer reads, editor
 * saves, every save is in the audit log.
 */
function PromoTokenPanel({ role }: { role: AdminRole }) {
  const view = useResource((signal) => {
    void signal;
    return adminReads.promoToken();
  }, []);
  const [cap, setCap] = useState<string | null>(null);
  const [starts, setStarts] = useState<string | null>(null);
  const [ends, setEnds] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const data = view.data;
  if (view.loading && data == null) return <Spinner />;
  if (view.error !== null) return <ErrorNote error={view.error} onRetry={() => view.reload()} />;
  if (data == null) return null;

  const s = data.settings;
  const capValue = cap ?? String(s.cap_per_day);
  const startsValue = starts ?? (s.starts_at === null ? "" : toLocalInput(s.starts_at));
  const endsValue = ends ?? (s.ends_at === null ? "" : toLocalInput(s.ends_at));
  const save = () => {
    setSaving(true);
    setError(null);
    void adminWrites
      .updatePromoToken({ cap_per_day: Number(capValue), starts_at: fromUtcInput(startsValue) ?? null, ends_at: fromUtcInput(endsValue) ?? null })
      .then(() => {
        setCap(null);
        setStarts(null);
        setEnds(null);
        view.reload();
      })
      .catch((cause: unknown) => setError(cause))
      .finally(() => setSaving(false));
  };
  const state = PROMO_STATE[data.state];

  return (
    <Panel
      title="Free energy"
      subtitle="An address we dropped the tenergy.app token on gets 65,000 energy for 1 h once — by signing on tenergy.me or by forwarding the token. Times are UTC; the cap counts claims per UTC day."
      action={<Pill value={state.label} tone={state.tone} />}
    >
      {data.token_id === null ? <Note>No promo token is configured on this network, so nobody can claim.</Note> : null}
      <div class="mb-3 grid gap-3 sm:grid-cols-4">
        <Field label="Cap per UTC day" hint={`${count(data.redeemed_today)} claimed today`}>
          <input class={inputClass} inputMode="numeric" value={capValue} disabled={role !== "editor"} onInput={(e) => setCap((e.currentTarget as HTMLInputElement).value)} />
        </Field>
        <Field label="Starts (UTC)" hint="Empty: already open">
          <input type="datetime-local" class={inputClass} value={startsValue} disabled={role !== "editor"} onInput={(e) => setStarts((e.currentTarget as HTMLInputElement).value)} />
        </Field>
        <Field label="Ends (UTC)" hint="Empty: no end">
          <input type="datetime-local" class={inputClass} value={endsValue} disabled={role !== "editor"} onInput={(e) => setEnds((e.currentTarget as HTMLInputElement).value)} />
        </Field>
        {role === "editor" ? (
          <FieldAction>
            <Button disabled={saving || !/^\d+$/.test(capValue)} onClick={save}>
              Save
            </Button>
          </FieldAction>
        ) : null}
      </div>
      {error === null ? null : <ErrorNote error={error} />}
      <Table
        rows={data.items.length}
        empty="Nobody has claimed the promo energy yet."
        head={
          <tr>
            <Th>Address</Th>
            <Th>Way</Th>
            <Th>Claimed (UTC)</Th>
            <Th>Grant</Th>
            <Th>Order</Th>
          </tr>
        }
      >
        {data.items.map((r) => (
          <tr key={r.address}>
            <Td>
              <Address value={r.address} />
            </Td>
            <Td>{r.method === "signature" ? "signed on the site" : "forwarded the token"}</Td>
            <Td mono>{utc(r.redeemed_at)}</Td>
            <Td>
              <Pill value={r.status === "delegated" ? "delegated" : r.status === "failed" ? "failed" : "on its way"} tone={r.status === "delegated" ? "good" : r.status === "failed" ? "bad" : "moving"} />
            </Td>
            <Td>{r.order_id === null ? <Blank /> : <ShortId value={r.order_id} />}</Td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

/**
 * The automatic tenergy.dev drops (docs/product/promo-redeem.md §Automatic drops, owner 2026-10-19): every new account
 * on Nile gets the token right after its activation, from our senders in turn. Off by default (owner: «рассылка должна
 * быть по дефолту выключена в админке»); the worker reads the switch per block, so no restart. It also needs the
 * worker's PROMO_DROPS_ENABLED and the senders file on the Nile server.
 */
function PromoDropsPanel({ role, view, target, withSwitch }: { role: AdminRole; view: Resource<PromoDropsView>; target: ApiTarget; withSwitch: boolean }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const data: PromoDropsView | null | undefined = view.data;
  if (view.loading && data == null) return <Spinner />;
  if (view.error !== null) return <ErrorNote error={view.error} onRetry={() => view.reload()} />;
  if (data == null) return null;

  const flip = () => {
    setSaving(true);
    setError(null);
    void adminWrites
      .setPromoDrops({ enabled: !data.enabled }, target)
      .then(() => view.reload())
      .catch((cause: unknown) => setError(cause))
      .finally(() => setSaving(false));
  };
  const pill = !data.enabled
    ? { label: "off", tone: "neutral" as const }
    : data.stopped_at !== null
      ? { label: "stopped: no sender bandwidth", tone: "attention" as const }
      : { label: "on", tone: "good" as const };

  return (
    <Panel
      title={withSwitch ? "Automatic drops" : "Drops"}
      subtitle={
        withSwitch
          ? "Every new account gets the token right after its activation, once per address, from our senders in turn. A sender without 300 free bandwidth means the drop is skipped and the same sender waits. Manual sends are listed here too."
          : "Every send of this token, manual ones included, from our senders in turn."
      }
      action={withSwitch ? <Pill value={pill.label} tone={pill.tone} /> : undefined}
    >
      <div class="mb-3 grid gap-3 sm:grid-cols-4">
        <Field label="Last 24 h" hint={`${count(data.skips_24h)} skipped`}>
          <div class="py-2">{count(data.drops_24h)} sent</div>
        </Field>
        <Field label="Next sender" hint={data.stopped_at === null ? "In turn, from the first" : `Stopped since ${utc(data.stopped_at)} UTC`}>
          <div class="py-2">#{data.next_sender + 1}</div>
        </Field>
        <Field label="Switched" hint={data.updated_by ?? "never"}>
          <div class="py-2 font-mono">{utc(data.updated_at)}</div>
        </Field>
        {role === "editor" && withSwitch ? (
          <FieldAction>
            <Button tone={data.enabled ? "danger" : "primary"} disabled={saving} onClick={flip}>
              {data.enabled ? "Switch off" : "Switch on"}
            </Button>
          </FieldAction>
        ) : null}
      </div>
      {error === null ? null : <ErrorNote error={error} />}
      <Table
        rows={data.drops.length}
        empty="No drops yet."
        head={
          <tr>
            <Th>Address</Th>
            <Th>Why</Th>
            <Th>Sender</Th>
            <Th>Sent (UTC)</Th>
            <Th>Transaction</Th>
          </tr>
        }
      >
        {data.drops.map((d) => (
          <tr key={d.txid}>
            <Td>
              <Address value={d.address} />
            </Td>
            <Td>
              <DropWhy context={d.context ?? null} />
            </Td>
            <Td>
              <Address value={d.sender} />
            </Td>
            <Td mono>{utc(d.sent_at)}</Td>
            <Td>
              <ShortId value={d.txid} />
            </Td>
          </tr>
        ))}
      </Table>
      {data.skips.length === 0 ? null : (
        <Table
          rows={data.skips.length}
          empty=""
          head={
            <tr>
              <Th>Skipped address</Th>
              <Th>Why</Th>
              <Th>At (UTC)</Th>
            </tr>
          }
        >
          {data.skips.map((k, i) => (
            <tr key={`${k.address}-${i}`}>
              <Td>
                <Address value={k.address} />
              </Td>
              <Td>{k.reason}</Td>
              <Td mono>{utc(k.at)}</Td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}

const WHY_LABEL: Record<DropContext["why"], string> = {
  new_account: "New account",
  manual: "Manual send",
  script: "Script",
  payer: "Payer",
  client: "Account client",
  activator: "Activator",
};

/**
 * Why an address got the token (owner 2026-10-24: «видно что адрес клиент какой-то площадки или активатор этого
 * клиента»): payer / account client of which venues, or the activator of which address; the week's spend decile, and
 * Tier 0 when the top fleet sent it.
 */
function DropWhy({ context }: { context: DropContext | null }) {
  if (context === null) return <span class="text-ink-muted">—</span>;
  const venues = context.venues !== undefined && context.venues.length > 0 ? context.venues.join(", ") : null;
  return (
    <span class="text-[12px]">
      {WHY_LABEL[context.why] ?? context.why}
      {context.why === "activator" && context.of !== undefined ? (
        <>
          {" of "}
          <Address value={context.of} />
        </>
      ) : null}
      {venues === null ? null : <span>{context.why === "activator" ? ` (${venues})` : ` · ${venues}`}</span>}
      {context.decile === undefined ? null : (
        <span class="block text-[11px] text-ink-muted">
          D{context.decile}
          {context.tier0 === true ? " · Tier 0" : ""}
        </span>
      )}
    </span>
  );
}
